import { useState } from 'react'
import { RootStateOrAny, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ActivityEvent, Status } from '../../enum'
import { useDateFormatter } from '../../hooks/useDateFormatter'
import ArchiveModal from '../Project/ProjectDetail/ArchiveModal'

interface Props {
  item: Activity
  isDashboard?: boolean
}

const ActivityItem = ({ item, isDashboard = false }: Props) => {
  const history = useHistory()

  const [showArchiveModal, setShowArchiveModal] = useState(false)

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  const createdAt = useDateFormatter(item.createdAt)

  const handleClick = () => {
    if (item.projectStatus === Status.DELETED) {
      return
    }

    if (item.projectStatus === Status.NEW) {
      history.push(`/project/edit/${item.project}`)
    } else {
      history.push(`/myquality/project/${item.project}`)
    }
  }

  const Message = () => {
    return isDashboard ? (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> commented on{' '}
            <span className="underline" onClick={handleClick}>
              {item.projectName}
            </span>
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    ) : (
      <div className={item.isAdmin ? 'reverse' : ''} style={{ width: '100%' }}>
        <div className="message m-b-10">{item.message}</div>

        <div className="flex flip space-between align-center">
          <div className="flex flip fs12">
            <div className="name-dot">
              <span>{item.userInitials}</span>
            </div>
            <strong>{item.userName}</strong>
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const Created = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> created{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const SubmittedForReview = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> submitted{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>{' '}
            for AE review
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const QuoteSubmitted = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> submitted service agreement for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            . Please sign and review charges.
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const QuoteUpdated = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has revised service agreement or
            charges for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            . Please review.
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const NewDocument = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> uploaded new documents to{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            .
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const NewPODocuments = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has uploaded new purchase order
            documents for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            .
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const AEAcceptsPODocuments = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has reviewed and accepted purchase
            order documentation for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            .
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const CustomerSubmittedPayment = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> submitted payment for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            .
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const CustomerSignedSA = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> signed service agreement for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              {item.projectName}
            </span>
            .
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const DeliverablesSubmitted = () => {
    return (
      <div className={`flex reverse`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has submitted deliverables for{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              <strong>{item.projectName}</strong>
            </span>
            . Please review and accept within five business days.
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const ProjectCompleted = () => {
    return (
      <div className={`flex reverse`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has accepted deliverables and
            marked{' '}
            <span
              className={
                item.projectStatus === Status.DELETED ? 'deleted' : 'underline'
              }
              onClick={handleClick}
            >
              <strong>{item.projectName}</strong>
            </span>{' '}
            as complete.
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const Deleted = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has deleted{' '}
            <strong>{item.projectName}</strong>. This project will no longer be
            available in your Project Library.
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  const Archived = () => {
    return (
      <div className={`flex ${item.isAdmin ? 'reverse' : ''}`}>
        <div className="name-dot">
          <span>{item.userInitials}</span>
        </div>
        <div className="flex-grow">
          <div className="fs12 m-b-10">
            <strong>{item.userName}</strong> has changed{' '}
            <strong>{item.projectName}</strong> to{' '}
            <strong>
              <em>Archived</em>
            </strong>
            .{' '}
            {userInfo.isAdmin && (
              <strong>
                <span
                  className="blue cursor"
                  onClick={() => setShowArchiveModal(true)}
                >
                  View details.
                </span>
              </strong>
            )}
          </div>
          <div className="fs10 gray-text">{createdAt}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="activity">
      {item.event === ActivityEvent.MESSAGE && <Message />}
      {item.event === ActivityEvent.CREATED && <Created />}
      {item.event === ActivityEvent.SUBMITTED_FOR_REVIEW && (
        <SubmittedForReview />
      )}
      {item.event === ActivityEvent.QUOTE_SUBMITTED && <QuoteSubmitted />}
      {item.event === ActivityEvent.QUOTE_UPDATED && <QuoteUpdated />}
      {item.event === ActivityEvent.NEW_DOCUMENT && <NewDocument />}
      {item.event === ActivityEvent.CUST_ADD_PO && <NewPODocuments />}
      {item.event === ActivityEvent.AE_ACCEPTS_PO && <AEAcceptsPODocuments />}
      {item.event === ActivityEvent.CUST_SUBMIT_PAYMENT && (
        <CustomerSubmittedPayment />
      )}
      {(item.event === ActivityEvent.CUST_SIGNED_AC ||
        item.event === ActivityEvent.CUST_SIGNED_SA) && <CustomerSignedSA />}
      {item.event === ActivityEvent.AE_SUBMITS_DELIVERABLES && (
        <DeliverablesSubmitted />
      )}
      {item.event === ActivityEvent.PROJECT_COMPLETED && <ProjectCompleted />}
      {item.event === ActivityEvent.DELETED && <Deleted />}
      {item.event === ActivityEvent.PROJECT_ARCHIVED && <Archived />}

      <ArchiveModal
        showArchiveModal={showArchiveModal}
        setShowArchiveModal={setShowArchiveModal}
        editMode={false}
      />
    </div>
  )
}

export default ActivityItem
